import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import { seriesKey } from '~/backend/connector-next-api/seriesKey';
import { Separated } from '~/components/Separated';
import type { WorkflowStepId } from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import type { OpenIntent } from '~/hooks/openInViewer/resolveOpenIntent';
import { openableDatasetKind, type SeriesDayGroup } from '../model';
import DatasetRow, {
    type DatasetInfoOffer,
    type DatasetPrimary,
} from './DatasetRow';

/** Dataset details on demand, with the key of the row showing them and of the row pinning them. */
type DatasetInfoProps = {
    info?: DatasetInfoOffer;
    infoOpenKey?: string;
    infoPinnedKey?: string;
};

/**
 * Row modes: `open` (the patient panel, where every row opens itself),
 * `select` (the import review, where choosing what to import is the task),
 * `imported` (sent series), `readOnly` (plain listing). Within a selection,
 * `alreadyStored` rows the record already holds are decided per row.
 */
type DatasetDayGroupProps = (
    | {
          group: SeriesDayGroup;
          mode: 'open';
          onOpenObject: (intent: OpenIntent) => void;
          stageWorkflow: WorkflowStepId | null;
          openBusy: boolean;
          primary?: DatasetPrimary;
      }
    | {
          group: SeriesDayGroup;
          mode?: 'select';
          checked: ReadonlySet<string>;
          onToggle: (seriesId: string) => void;
          alreadyStored?: ReadonlySet<string>;
      }
    | {
          group: SeriesDayGroup;
          mode: 'imported' | 'readOnly';
      }
) &
    DatasetInfoProps;

/** Sticky day header (label, modalities held, count) plus the day's dataset rows. */
export default function DatasetDayGroup(props: DatasetDayGroupProps) {
    const { group, info, infoOpenKey, infoPinnedKey } = props;
    const selection = 'checked' in props ? props : undefined;
    const opening = props.mode === 'open' ? props : undefined;
    return (
        <Box>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    height: 40,
                    px: 3,
                    fontSize: 13,
                    color: 'text.secondary',
                    bgcolor: 'background.raised',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Box
                    component="span"
                    sx={{
                        fontFamily: 'fontFamilyMonospace',
                        fontWeight: 500,
                        color: 'text.primary',
                    }}
                >
                    {group.label}
                </Box>
                <Separated>
                    {group.modalities.map((modality) => (
                        <Box
                            key={modality}
                            component="span"
                            sx={{ letterSpacing: '0.04em' }}
                        >
                            {modality}
                        </Box>
                    ))}
                </Separated>
                <Box
                    component="span"
                    sx={{
                        fontFamily: 'fontFamilyMonospace',
                        ml: 'auto',
                    }}
                >
                    <FormattedMessage
                        id="patientList.drilldown.datasetCount"
                        values={{ count: group.seriesCount }}
                    />
                </Box>
            </Box>
            {group.nodes.map((node, index) => (
                <Box key={rowKey(node.series, group.day, index)}>
                    {node.referencedImageMissing && (
                        // Never the referenced Series Instance UID: a DICOM UID
                        // is PHI, and what the reader needs is that the image
                        // is not here, not which one it was.
                        <Typography
                            sx={{
                                pl: 6,
                                pt: 0.5,
                                fontSize: 10,
                                color: 'action.disabled',
                            }}
                        >
                            <FormattedMessage id="patientList.drilldown.imageNotHere" />
                        </Typography>
                    )}
                    {row(node.series, group.day, index)}
                    {node.structureSets.length > 0 && (
                        <Box sx={{ pl: 3 }}>
                            <Typography
                                sx={{
                                    pl: 3,
                                    pt: 0.5,
                                    fontSize: 10,
                                    color: 'action.disabled',
                                }}
                            >
                                <FormattedMessage id="patientList.drilldown.contouredOnThisImage" />
                            </Typography>
                            {node.structureSets.map((set, setIndex) => (
                                <Box key={rowKey(set, group.day, setIndex)}>
                                    {row(set, group.day, setIndex, true)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );

    /**
     * One dataset row in whichever mode this group is rendering.
     *
     * `nested` says the row sits under the image it was contoured on, which
     * is the whole test for whether a structure set can be opened: nesting
     * happened only because that image is among the patient's series. A
     * structure set standing at the top level has no image to be drawn on —
     * it names none, or names one the patient does not have — and the viewer
     * takes the image FROM the set, so opening it could only end in an error
     * about the dataset the user chose.
     */
    function row(
        series: SeriesDto,
        day: string,
        index: number,
        nested = false
    ) {
        const key = rowKey(series, day, index);
        const infoProps = {
            infoKey: key,
            info,
            infoOpen: infoOpenKey === key,
            infoPinned: infoPinnedKey === key,
        };
        if (opening !== undefined) {
            const orphanSet = !nested && series.datasetType === 'StructureSet';
            // A dose, plan or registration is shown WITH the image it belongs
            // to; nothing displays one on its own. Treating every row that is
            // not a structure set as an image offered Contouring on a
            // registration and addressed the open to a dataset no view shows.
            const opensOnItsOwn = openableDatasetKind(series) !== null;
            return (
                <DatasetRow
                    key={key}
                    series={series}
                    control="open"
                    onOpenObject={opening.onOpenObject}
                    stageWorkflow={opening.stageWorkflow}
                    openBusy={opening.openBusy}
                    primary={opening.primary}
                    openRefusalId={
                        orphanSet
                            ? 'patientList.drilldown.cannotOpenNoImage'
                            : !opensOnItsOwn
                              ? 'patientList.drilldown.cannotOpenRtObject'
                              : undefined
                    }
                    {...infoProps}
                />
            );
        }
        // Selection is keyed by a real series id; id-less series are not selectable.
        const seriesId = seriesKey(series);
        if (selection === undefined || seriesId === undefined) {
            return (
                <DatasetRow
                    key={key}
                    series={series}
                    control={props.mode === 'imported' ? 'imported' : 'none'}
                    {...infoProps}
                />
            );
        }
        if (selection.alreadyStored?.has(seriesId)) {
            return (
                <DatasetRow
                    key={key}
                    series={series}
                    control="alreadyStored"
                    {...infoProps}
                />
            );
        }
        return (
            <DatasetRow
                key={key}
                series={series}
                control="select"
                seriesId={seriesId}
                checked={selection.checked.has(seriesId)}
                onToggle={selection.onToggle}
                {...infoProps}
            />
        );
    }
}

const rowKey = (series: SeriesDto, day: string, index: number): string =>
    seriesKey(series) ?? `${day}-${index}`;
